import { useState } from "react";

import { IHospitalDetail } from "../../../../@types/admin/AdminHospitalDetails";
import { IWard } from "../../../../@types/admin/AdminWard";
import { useAddHospitalStaff } from "../../../../hooks/mutations/admin/hospitalDetails";
import { useRoleOptions } from "../../../../hooks/queries/admin/selectValues";
import { useToast } from "../../../../hooks/useToast";
import { errorToastMessage } from "../../../../utils/HttpError";
import {
  AdminSelectField,
  CheckboxField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  WarningNotice,
} from "../../shared";

interface IAddStaffModalProps {
  hospital: IHospitalDetail;
  wards: IWard[];
  onDone: () => void;
}

/**
 * The two levels Retool offered. Note the labels invert the values: "Ward" is
 * `hospital` and "Hospital" is `hospital-group`, because `cms_hospital` is a
 * ward. `hospital-group-hierarchy` is a third level the API accepts and Retool
 * did not offer; it is not added here either, since nothing says who should
 * get it.
 */
const PERMISSION_LEVELS = [
  {
    value: "hospital",
    label: "Ward",
    hint: "Reaches only the patients of the ward chosen below.",
  },
  {
    value: "hospital-group",
    label: "Hospital",
    hint: "Reaches every patient of every ward in this hospital.",
  },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** E.164, which the API enforces on staff numbers (EN-5121). */
const E164_PATTERN = /^\+[1-9]\d{1,14}$/;

/**
 * Adds a staff member to the hospital — Retool's `drawerFrame_addStaff`
 * (EN-5175).
 *
 * ⚠️ **Retool's version of this form has been broken since EN-4961.**
 * `AddStaffRequestDto.Role` is `[Required]` and validated against
 * `RoleCodes.All`, and Retool's request body sends eleven keys, none of them
 * `role` — so every submission fails with "Role is required". The port adds the
 * Role select the form always needed, reading
 * `GET /Retool/SelectValues/Roles`, which is filtered to the portal-assignable
 * roles so `super-admin` can never be granted here (EN-5150).
 *
 * ⚠️ This creates an account that can read patient data. The permission level
 * decides how much.
 */
export function AddStaffModal({
  hospital,
  wards,
  onDone,
}: IAddStaffModalProps) {
  const { showToast } = useToast();
  const { roles } = useRoleOptions();
  const { addStaff, isAddingStaff } = useAddHospitalStaff();

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [wardId, setWardId] = useState("");
  const [permissionLevel, setPermissionLevel] = useState("hospital");
  const [role, setRole] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [healthProviderId, setHealthProviderId] = useState("");
  const [receiveAlerts, setReceiveAlerts] = useState(false);
  const [archiveMeasurements, setArchiveMeasurements] = useState(false);
  const [activeAccount, setActiveAccount] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [touched, setTouched] = useState(false);

  const emailError = (() => {
    if (!email.trim()) return "Email is required.";
    if (!EMAIL_PATTERN.test(email.trim())) {
      return "That doesn't look like an email address.";
    }
    return undefined;
  })();

  const phoneError =
    phoneNumber.trim() && !E164_PATTERN.test(phoneNumber.trim())
      ? "Use E.164 format, e.g. +353871234567."
      : undefined;

  const errors = {
    email: emailError,
    firstName: firstName.trim() ? undefined : "First name is required.",
    lastName: lastName.trim() ? undefined : "Last name is required.",
    // Required at every level: the DTO validates WardId > 0 regardless, and
    // only adds the "required when hospital" rule on top.
    wardId: wardId ? undefined : "Ward is required.",
    role: role ? undefined : "Role is required.",
    phoneNumber: phoneError,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const selectedLevel = PERMISSION_LEVELS.find(
    (level) => level.value === permissionLevel
  );

  const handleSubmit = async () => {
    setTouched(true);
    if (hasErrors) return;

    try {
      await addStaff({
        request: {
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          permissionLevel,
          // The hospital GROUP — Retool sent formHospitalDetails.data.id here.
          hospitalId: Number(hospital.id),
          wardId: Number(wardId),
          role,
          phoneNumber: phoneNumber.trim() || null,
          healthProviderId: healthProviderId.trim() || null,
          receiveAlerts,
          archiveMeasurements,
          activeAccount,
          isAdmin,
        },
      });
      showToast("Staff successfully added.", "success");
      onDone();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error adding this staff member."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Add staff</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Creating a staff account in <strong>{hospital.name}</strong>.
        </ModalBodyText>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-email">Email</FilterLabel>
          <FilterInput
            id="add-staff-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {touched && errors.email && <FieldError>{errors.email}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-first-name">First name</FilterLabel>
          <FilterInput
            id="add-staff-first-name"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          {touched && errors.firstName && (
            <FieldError>{errors.firstName}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-last-name">Last name</FilterLabel>
          <FilterInput
            id="add-staff-last-name"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
          {touched && errors.lastName && (
            <FieldError>{errors.lastName}</FieldError>
          )}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-role">Role</FilterLabel>
          <AdminSelectField
            id="add-staff-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="">Select a role</option>
            {roles.map((option) => (
              <option key={option.code} value={option.code}>
                {option.name}
              </option>
            ))}
          </AdminSelectField>
          {touched && errors.role && <FieldError>{errors.role}</FieldError>}
          <FieldHint>
            Required by the API, and missing from Retool&apos;s form — which is
            why adding staff there fails.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-level">Permission level</FilterLabel>
          <AdminSelectField
            id="add-staff-level"
            value={permissionLevel}
            onChange={(e) => setPermissionLevel(e.target.value)}
          >
            {PERMISSION_LEVELS.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </AdminSelectField>
          {selectedLevel && <FieldHint>{selectedLevel.hint}</FieldHint>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-ward">Ward</FilterLabel>
          <AdminSelectField
            id="add-staff-ward"
            value={wardId}
            onChange={(e) => setWardId(e.target.value)}
          >
            <option value="">Select a ward</option>
            {wards.map((ward) => (
              <option key={ward.id} value={String(ward.id)}>
                {ward.name}
              </option>
            ))}
          </AdminSelectField>
          {touched && errors.wardId && <FieldError>{errors.wardId}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-phone">Phone number</FilterLabel>
          <FilterInput
            id="add-staff-phone"
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          {touched && errors.phoneNumber && (
            <FieldError>{errors.phoneNumber}</FieldError>
          )}
          <FieldHint>
            E.164 — it feeds the alert SMS and voice calls directly (EN-5121).
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="add-staff-health-provider-id">
            Health provider ID
          </FilterLabel>
          <FilterInput
            id="add-staff-health-provider-id"
            value={healthProviderId}
            maxLength={64}
            onChange={(e) => setHealthProviderId(e.target.value)}
          />
          <FieldHint>
            Optional. Transmural Platform orders for this HCP are matched on it.
          </FieldHint>
        </FilterGroup>

        <FilterGroup>
          <CheckboxField>
            <input
              type="checkbox"
              checked={activeAccount}
              onChange={(e) => setActiveAccount(e.target.checked)}
            />
            Active
          </CheckboxField>
          <CheckboxField>
            <input
              type="checkbox"
              checked={receiveAlerts}
              onChange={(e) => setReceiveAlerts(e.target.checked)}
            />
            Receive alerts
          </CheckboxField>
          <CheckboxField>
            <input
              type="checkbox"
              checked={archiveMeasurements}
              onChange={(e) => setArchiveMeasurements(e.target.checked)}
            />
            Can archive measurements
          </CheckboxField>
          <CheckboxField>
            <input
              type="checkbox"
              checked={isAdmin}
              onChange={(e) => setIsAdmin(e.target.checked)}
            />
            Admin
          </CheckboxField>
        </FilterGroup>

        <WarningNotice>
          This creates an account that can read patient data.{" "}
          {selectedLevel?.hint}
        </WarningNotice>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isAddingStaff}
            onClick={onDone}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isAddingStaff}
            onClick={handleSubmit}
          >
            {isAddingStaff ? "Adding..." : "Add staff"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
